import React from 'react'

import { Box } from '@mui/material'
import type { SxProps, Theme } from '@mui/material'
import type { LucideIcon } from 'lucide-react'

import { border } from 'settings/tokens'

/** Diameter of the badge. The gift/scan success badge's 88 is the reference. */
const CIRCLE = 88
/**
 * Glyph size inside it. 40/88 — the ratio the success badge already used. The
 * two error boundaries drew 28-in-56, the same ratio at a smaller diameter, so
 * scaling them up to 88 keeps the glyph's weight on the disc identical.
 */
const GLYPH = 40

/**
 * What the icon COLOUR says. It is the only thing a tone changes.
 *
 * - `default` — a state to explain. The brand accent.
 * - `alert`   — something is actually broken or blocked. `palette.error.main`.
 *
 * The split is "is something wrong" vs "is this a condition". A retired tag, an
 * unissued certificate and a mailed-but-unconfirmed tag are all CONDITIONS, and
 * their pages deliberately carry no accusation (read those files' headers), so
 * they stay on the brand tone. A crash, a suspended account and a scan that
 * could not be resolved are failures, and get the red.
 */
export type IconBadgeTone = 'default' | 'alert'

/**
 * The one place the tone becomes a colour. Both the glyph and the ring read it,
 * so a new tone cannot arrive with a red glyph and a brand-coloured halo.
 *
 * `brand[600]` is not Vera purple in this app: `settings/mui/brandRamp.ts` derives
 * the ramp from the tenant's Studio `primary_color`, and step 600 is that colour
 * unmixed. The `|| primary.main` arm is the guard `ScanTag`'s `brandColor` already
 * documents — if a theme layer forgets the ramp, fall through to the tenant's
 * primary rather than to `@repo/ui`'s purple.
 */
const toneColor = (theme: Theme, tone: IconBadgeTone) =>
  tone === 'alert'
    ? theme.palette.error.main
    : (theme.palette as any).brand?.[600] || theme.palette.primary.main

/**
 * The wave. Numbers taken verbatim from `ScanTag`'s armed-chip pulse so the two
 * surfaces are one motion rather than two that resemble each other: 3s ease-out,
 * `scale(1) / opacity .45` out to `scale(1.6) / opacity 0`.
 *
 * 3s sounds far too long for an arrival, and is not, because ease-out front-loads
 * it. Measured by scrubbing the animation's `currentTime`: 31% of the travel is
 * done at 600ms, 68% at 1.5s, and opacity falls on the same curve. So the wave's
 * visible energy overlaps the badge's own 260ms entrance and the remainder is a
 * ghost dissolving, which is why one 3s ring reads as "this arrived" rather than
 * as "something is still happening".
 */
const RING_MS = 3000
/**
 * Half a cycle. Only used in the continuous mode, where it is what makes two rings
 * a steady ripple instead of a strobe.
 */
const RING_STAGGER_MS = RING_MS / 2

/**
 * The badge's own entrance.
 *
 * 260ms on `cubic-bezier(0.32, 0.72, 0, 1)` is this app's single "content arrives"
 * motion (`utils/pageTransition`'s `PAGE_SLIDE_MS` + curve, which `ScanTag`'s
 * staged reveal already reuses as literals for the same reason). Deliberately NOT
 * `veraScanSuccessPop`'s `cubic-bezier(.34,1.56,.64,1)` overshoot: that bounce is
 * the success celebration, and this badge also opens "Something went wrong" and
 * "Account Suspended", where a springy pop is the wrong register.
 *
 * 0.92 rather than a bigger scale for the same reason — it should read as the badge
 * settling into place, not as it being thrown at the screen.
 */
const ENTER_MS = 260
const ENTER_EASE = 'cubic-bezier(0.32, 0.72, 0, 1)'
const ENTER_SCALE = 0.92

interface IconBadgeProps {
  /**
   * A lucide glyph, passed as the COMPONENT rather than an element, so this
   * component owns its size, stroke and colour. That is deliberate: passing a
   * rendered element would put three drift vectors back at the call sites, and
   * five hand-rolled copies of this badge is why it exists.
   */
  icon?: LucideIcon
  /**
   * Escape hatch for a glyph lucide cannot express — today only
   * `SuccessState`'s self-drawing check and its pending ring. Ignored when
   * `icon` is set. Inherits `color`, so a plain `currentColor` SVG is toned by
   * the `tone` prop exactly as a lucide glyph is.
   */
  children?: React.ReactNode
  tone?: IconBadgeTone
  /** Diameter override. The glyph scales with it, so the ratio is preserved. */
  size?: number
  /**
   * ⚠️ CURRENTLY INERT, and measured to be — do not reach for it expecting a
   * visible change. The app's global stylesheet carries `svg, svg *
   * { stroke-width: 1.25 !important }`, which beats the attribute lucide
   * writes, so EVERY glyph in this app renders at 1.25 whatever is passed
   * here. Verified in the browser: a badge authored at 1.5 computes to
   * `1.25px`.
   *
   * It is kept, rather than deleted, for exactly one reason: it lets a caller
   * record the weight it MEANT. The gift drawer's check was authored at 2 to
   * match the one `SuccessState` draws, and if that global override is ever
   * lifted the two must not silently diverge into a 1.5 and a 2 for the same
   * moment in the same flow. Today the override makes them agree for free.
   *
   * So: 1.5 is the default because it is what the rest of the app authors. Do
   * not use this knob to make a glyph "pop" — it cannot, and if the override
   * goes away it would then be an inconsistency rather than a no-op.
   */
  strokeWidth?: number
  /**
   * KEEP THE RING LOOPING, because this screen is waiting for the user to scan.
   *
   * Named for the state it describes rather than for what it draws (`pulse`)
   * deliberately: the prop is a claim about the SURFACE — something is genuinely
   * outstanding and the user is the one who has to resolve it — and the animation
   * is downstream of that claim. A caller reaching for `pulse` is choosing a
   * decoration; a caller reaching for `awaiting` has to be able to answer "waiting
   * for what?".
   *
   * ## Why the default is ONE wave and not this
   *
   * A looping ring is the live/pending idiom and this app already uses it that way
   * — `ScanTag`'s armed chip pulses for exactly as long as the phone has not yet
   * found the tag, and stops the moment it does. Spend it anywhere else and it
   * stops meaning anything.
   *
   * On a terminal state screen nothing is happening. A throbbing halo above
   * "There is nothing wrong with your item" argues with the sentence underneath
   * it — it says the page is still working on something when the page's entire
   * job is to be a settled answer. Worse on the alert tone, where the loop is red.
   * One wave gives the badge an arrival without claiming activity.
   *
   * As of writing NO page passes this. All twelve call sites are terminal states
   * or confirmations. It exists for the scan-waiting surfaces (`ScanTag`'s chip is
   * the one live example, and it keeps its own copy — see its header) and so that
   * the next one does not hand-roll a third pulse.
   */
  awaiting?: boolean
  sx?: SxProps<Theme>
}

/**
 * The grey circle every "here is the state of things" screen puts above its
 * title. One treatment, one place.
 *
 * ## Why it exists
 *
 * There were FIVE hand-rolls in TWO disagreeing variants (§36 — the rule is
 * that the second hand-roll is a shared component, and this was the fifth):
 *
 *   grey/brand, 88px : `giftSendDrawer`, `UI/SuccessState`
 *   red-tinted, 56px : `UI/ErrorBoundary`, `auth/AuthErrorBoundary`
 *   accent-tinted, 72px : `pages/NotFound`
 *
 * The two red ones were byte-identical copies of each other, down to a
 * hardcoded `#f44336` that ignored the tenant's theme entirely.
 *
 * ## Why the disc is ALWAYS grey, and the state lives in the glyph
 *
 * A tinted disc puts the state signal in a 88px block of colour, which on an
 * error page is the loudest thing on screen and reads as an alarm before the
 * reader has got to a single word. Most of these pages exist precisely to keep
 * someone from concluding that the luxury item in their hand is fake, so a red
 * disc actively works against the copy underneath it. The glyph carries the
 * state; the disc is furniture and stays the same on every screen, which is
 * what makes the family read as one product.
 *
 * A tinted disc is also unthemeable in practice: two of the three variants
 * derived their fill from a colour (`#f44336`, `alpha(accent, .1)`), so a
 * tenant's theme either was ignored or silently changed the badge's weight.
 *
 * ## The border is load-bearing
 *
 * Kept verbatim from `SuccessState`, whose comment gives the reason and the
 * reason is sound: without it the badge is a grey disc that dissolves into a
 * light sheet, and reads as a gap in the layout rather than an object sitting
 * on it. `!important` because `index.css` sets `#vera-shadow-root * {
 * border-color: hsl(var(--border)) }` at ID specificity and an `sx` border
 * colour is otherwise silently discarded — see the note on `border` in
 * `settings/tokens`. The SAME trap applies to the ring below, and there it eats
 * the tone rather than a hairline grey.
 *
 * ## The ring
 *
 * A wave radiates out from the disc as it arrives, in the tone's colour, then
 * stops. `awaiting` keeps it looping — read that prop's own note for why that is
 * opt-in and not the default. The wave's numbers are `ScanTag`'s, on purpose.
 */
export const IconBadge = ({
  icon: Icon,
  children,
  tone = 'default',
  size = CIRCLE,
  strokeWidth = 1.5,
  awaiting = false,
  sx,
}: IconBadgeProps) => {
  const glyph = Math.round((size * GLYPH) / CIRCLE)

  /*
   * ONE ring on arrival, TWO while awaiting.
   *
   * The second ring exists only to fill the gap in a loop: offset by half the
   * cycle, the pair reads as a continuous ripple where a single looping ring
   * reads as a strobe restarting. A one-shot has no gap to fill, so a second
   * ring there would just be two waves where the design calls for one.
   */
  const delays = awaiting ? [0, RING_STAGGER_MS] : [0]

  return (
    <Box
      sx={[
        (theme: Theme) => ({
          width: size,
          height: size,
          borderRadius: '50%',
          backgroundColor: theme.palette.grey[50],
          border: `1px solid ${border.subtle} !important`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          color: toneColor(theme, tone),
          // Anchor for anything positioned inside it — the rings below, and
          // `SuccessState`'s `PendingRing`, are absolute rather than flowed.
          position: 'relative',
          // Rests at its FINAL state, so an environment that never runs the
          // animation shows the badge rather than a hole. This app's standing
          // rule (`utils/pageTransition`, `SuccessState`) and not a preference:
          // the dev preview pane reports `visibilityState: 'hidden'` and freezes
          // animation clocks outright.
          opacity: 1,
          transform: 'scale(1)',
          animation: `veraBadgeEnter ${ENTER_MS}ms ${ENTER_EASE}`,
          '@keyframes veraBadgeEnter': {
            from: { opacity: 0, transform: `scale(${ENTER_SCALE})` },
            to: { opacity: 1, transform: 'scale(1)' },
          },
          '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {delays.map((delay, i) => (
        <Box
          key={delay}
          aria-hidden
          data-vera-badge-ring=''
          sx={(theme: Theme) => ({
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            pointerEvents: 'none',
            /*
             * ⚠️ `!important` is LOAD-BEARING, not defensive, and this is the
             * FIFTH time this trap has bitten in this app (see `settings/tokens`,
             * `MuiAlert.ts`, and `ScanTag`'s copy of this same ring).
             *
             * `index.css` sets `#vera-shadow-root * { border-color: … }`. An ID
             * selector is 0-1-0-0; the single class emotion compiles `sx` to is
             * 0-0-1-0. So the authored colour is parsed, computed, and then
             * discarded — the ring renders `rgb(228,228,231)` (#E4E4E7, the
             * `border.subtle` token) and the alert tone's red halo is silently a
             * grey one. It looks deliberate, which is why it keeps recurring.
             *
             * The property FORM does not matter — a separate `borderColor` loses
             * exactly as this shorthand does. Only the `!important` holds.
             */
            border: `1px solid ${toneColor(theme, tone)} !important`,
            /*
             * Rests INVISIBLE, unlike the badge above. The ring is not content:
             * once the wave has passed there is nothing for it to degrade to, and
             * a ring parked at `scale(1)` sits exactly on the disc's own 1px
             * border and double-paints into what looks like a heavy second edge.
             */
            opacity: 0,
            animation: `veraBadgeRing ${RING_MS}ms ease-out${awaiting ? ' infinite' : ''}`,
            animationDelay: `${delay}ms`,
            '@keyframes veraBadgeRing': {
              '0%': { transform: 'scale(1)', opacity: 0.45 },
              '100%': { transform: 'scale(1.6)', opacity: 0 },
            },
            /*
             * Motion off. The two modes degrade DIFFERENTLY, and the difference is
             * the same one the `awaiting` prop encodes:
             *
             * `awaiting` — keep the FIRST ring static at 0.45 and drop the second.
             *   The surface is still waiting, and a static tone-coloured ring still
             *   says "armed". Two of them pinned at `scale(1)` composite to ~0.7 on
             *   the same pixels and read as a heavy second border, which is why the
             *   second is hidden rather than merely stopped. `ScanTag`'s fallback,
             *   unchanged.
             *
             * default — NO ring. The one-shot is pure arrival emphasis; with the
             *   arrival gone there is nothing to emphasise, and leaving a permanent
             *   coloured halo would change what every one of these screens LOOKS
             *   like for motion-sensitive users — a standing red ring above "There
             *   is nothing wrong with your item" is the exact claim the one-shot
             *   default exists to avoid making.
             */
            '@media (prefers-reduced-motion: reduce)': {
              animation: 'none',
              opacity: awaiting && i === 0 ? 0.45 : 0,
            },
          })}
        />
      ))}
      {Icon ? <Icon size={glyph} strokeWidth={strokeWidth} /> : children}
    </Box>
  )
}

export default IconBadge
